import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Flex, Stack, useTheme, spacing } from '@fti/design-system';
import { useProjects } from '../../api/media-sim/projects';
import { ProjectFilters } from '../../components/media-sim/projects/ProjectFilters';
import { ProjectList } from '../../components/media-sim/projects/ProjectList';
import { useProjectRun } from '../../components/media-sim/layout/ProjectRunContext';
import { useMediaSimUser } from '../../components/media-sim/layout/MediaSimUserContext';

export function ProjectsPage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { selectProject } = useProjectRun();
  // `record.cognito_sub` is what the API compares against a project's
  // created_by, so it decides whether the Delete button is offered.
  const { isAdmin, record } = useMediaSimUser();

  // Opening a project selects it in the context bar, then lands on its Overview
  // sub-page (draft projects go to the wizard from there).
  const openProject = (id: string) => {
    selectProject(id);
    const project = projects?.find((p) => p.id === id);
    if (project?.status === 'draft') {
      navigate(`/simulations/projects/${id}/edit`);
    } else {
      navigate('/simulations/overview');
    }
  };

  const [status, setStatus] = useState('');
  const [scope, setScope] = useState<'mine' | 'all'>('mine');

  const { data: projects, loading, error, refetch } = useProjects({
    status: status || undefined,
    scope,
  });

  return (
    <Stack gap="md">
      <Flex justify="between" align="center">
        <h1 style={{ fontSize: '1.5rem', fontWeight: 600, color: theme.colors.text.primary, margin: 0 }}>
          Projects
        </h1>
        <Button
          variant="solid"
          color="primary"
          onClick={() => navigate('/simulations/projects/new')}
        >
          Create Project
        </Button>
      </Flex>

      <ProjectFilters
        status={status}
        onStatusChange={setStatus}
        scope={scope}
        onScopeChange={setScope}
        isAdmin={isAdmin}
      />

      {loading && (
        <Card variant="outlined">
          <Card.Body>
            <Flex justify="center" style={{ padding: spacing.lg }}>
              <p style={{ color: theme.colors.text.secondary, margin: 0 }}>Loading projects...</p>
            </Flex>
          </Card.Body>
        </Card>
      )}

      {error && (
        <Card variant="outlined">
          <Card.Body>
            <Stack gap="sm" style={{ padding: spacing.sm }}>
              <p style={{ color: theme.colors.semantic.error.default, margin: 0 }}>
                Failed to load projects: {error.message}
              </p>
              <Button variant="outline" size="sm" onClick={refetch}>
                Retry
              </Button>
            </Stack>
          </Card.Body>
        </Card>
      )}

      {!loading && !error && projects && (
        <ProjectList
          projects={projects}
          onOpenProject={openProject}
          currentUserSub={record?.cognito_sub}
          isAdmin={isAdmin}
          onProjectDeleted={refetch}
        />
      )}
    </Stack>
  );
}
